import { useRef, useCallback, useState } from 'react';

interface UseSwipePagesOptions {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  enabled?: boolean;
  threshold?: number;
}

interface UseSwipePagesReturn {
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
  };
  swipeOffset: number;
  isSwiping: boolean;
}

/**
 * Hook for swipe-left/right page navigation.
 * Handles angle detection (ignores vertical scrolls), zoom-aware disabling,
 * and provides visual offset feedback during the swipe.
 */
export function useSwipePages({
  currentPage,
  totalPages,
  onPageChange,
  enabled = true,
  threshold = 50,
}: UseSwipePagesOptions): UseSwipePagesReturn {
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const directionLockedRef = useRef<'horizontal' | 'vertical' | null>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || e.touches.length > 1) return;
      const touch = e.touches[0];
      startXRef.current = touch.clientX;
      startYRef.current = touch.clientY;
      directionLockedRef.current = null;
      setSwipeOffset(0);
    },
    [enabled]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || e.touches.length > 1) return;
      const touch = e.touches[0];
      const deltaX = touch.clientX - startXRef.current;
      const deltaY = touch.clientY - startYRef.current;

      if (!directionLockedRef.current) {
        const absDx = Math.abs(deltaX);
        const absDy = Math.abs(deltaY);
        if (absDx < 8 && absDy < 8) return;
        const angle = Math.atan2(absDy, absDx) * (180 / Math.PI);
        directionLockedRef.current = angle > 35 ? 'vertical' : 'horizontal';
      }

      if (directionLockedRef.current === 'vertical') return;

      const canGoLeft = currentPage > 1;
      const canGoRight = currentPage < totalPages;
      if ((deltaX > 0 && !canGoLeft) || (deltaX < 0 && !canGoRight)) {
        setSwipeOffset(deltaX * 0.15);
        return;
      }

      setIsSwiping(true);
      setSwipeOffset(deltaX * 0.6);
    },
    [enabled, currentPage, totalPages]
  );

  const handleTouchEnd = useCallback(() => {
    if (!enabled || directionLockedRef.current === 'vertical') {
      setSwipeOffset(0);
      setIsSwiping(false);
      return;
    }

    if (Math.abs(swipeOffset / 0.6) >= threshold) {
      if (swipeOffset > 0 && currentPage > 1) {
        onPageChange(currentPage - 1);
      } else if (swipeOffset < 0 && currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    }

    setSwipeOffset(0);
    setIsSwiping(false);
    directionLockedRef.current = null;
  }, [enabled, swipeOffset, threshold, currentPage, totalPages, onPageChange]);

  return {
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    swipeOffset,
    isSwiping,
  };
}
